<template>
  <div class="form-group row">
    <form-label :is-required="true">
      <span>{{ capitalize(i18n.t('names.audience', 1)) }}</span>
    </form-label>
    <div class="col-sm-3">
      <multiselect
        v-model="selectedExternalDemographicCategory"
        track-by="id"
        label="name"
        :options="filteredExternalDemographics"
        :searchable="true"
        :show-labels="false"
        :allow-empty="false" />
      <span
        v-show="validator.messageBag.errors.has('demo')"
        class="help is-danger"
        >{{ validator.messageBag.errors.first('demo') }}</span
      >
    </div>
    <div
      v-if="selectedExternalDemographicCategory"
      class="col-sm-6">
      <multiselect
        v-model="externalDemographic"
        track-by="id"
        label="nameWithParent"
        group-values="children"
        group-label="name"
        :options="selectedExternalDemographicCategory ? selectedExternalDemographicCategory.childrenWithParentName : []"
        :searchable="true"
        :show-labels="false"
        :allow-empty="false" />
      <span
        v-show="validator.messageBag.errors.has('demo')"
        class="help is-danger"
        >{{ validator.messageBag.errors.first('demo') }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { FormLabel } from '@/common/components/controls';
  import { DataProvider, ExternalDemographic } from '@/common/models';
  import AudienceRule from '@/common/models/audience/AudienceRule';
  import Validator from '@/common/models/audience/Validator';
  import { mapActions } from '@/utils/StateMapping';

  // Setup
  const i18n = useI18n();
  const { updateAudienceRuleExternalDemographicID, updateAudienceRuleName } = mapActions();
  const props = defineProps<{
    validator: Validator;
    selectedDataProvider: DataProvider;
    externalDemographicCategory: object;
    audienceRule: AudienceRule;
  }>();
  const emit = defineEmits<{
    (e: 'externalDemographicCategoryChanged', val: ExternalDemographic): void;
  }>();

  // Computed
  const filteredExternalDemographics = computed<ExternalDemographic[]>(() => {
    if (props.selectedDataProvider) {
      return props.selectedDataProvider.externalDemographics;
    }
    return [];
  });
  const selectedExternalDemographicCategory = computed({
    get() {
      if (props.externalDemographicCategory) {
        return props.externalDemographicCategory;
      }
      if (!props.audienceRule) {
        return null;
      }
      let toReturn = null;
      filteredExternalDemographics.value.forEach((level1Demo) => {
        level1Demo.childrenWithParentName.forEach((level2Demo) => {
          level2Demo.childrenWithParentName.forEach((level3Demo) => {
            if (level3Demo.id === props.audienceRule.externalDemographicID) {
              toReturn = level1Demo;
            }
          });
        });
      });
      return toReturn;
    },
    set(value) {
      emit('externalDemographicCategoryChanged', value);
    },
  });
  const externalDemographic = computed({
    get() {
      if (props.audienceRule && selectedExternalDemographicCategory.value) {
        const flatChildrenList = selectedExternalDemographicCategory.value.childrenWithParentName
          .map((x) => x.childrenWithParentName)
          .flat();
        return flatChildrenList.find((x) => x.id === props.audienceRule.externalDemographicID);
      }
      return null;
    },
    set(value) {
      if (props.audienceRule === null || props.audienceRule === undefined) {
        return;
      }
      updateAudienceRuleExternalDemographicID({
        audienceRule: props.audienceRule,
        value: value.id,
      });
      updateAudienceRuleName({
        audienceRule: props.audienceRule,
        value: `${selectedExternalDemographicCategory.value.name} - ${value.name}`,
      });
    },
  });
</script>
